/**
 * Numbers and pure edits for the Edit board modal (Boards Redesign list 5,
 * cards 5.1-5.4, 12.10.2026). No store access: the modal is opened from an
 * open board (assembled, tasks in memory) and from a grid tile (a summary
 * with no tasks), so everything here takes the assembled board or nothing.
 */
import { boardProgress } from './boardProgress';
import { paletteOf, labelIdsOf } from './boardLabels';

/**
 * How many tasks carry each label: `{ [labelId]: count }`, every palette entry
 * present (0 when unused). Done tasks count too — a label is "in use" while any
 * card holds it, which is what you need to know before deleting it (Linear and
 * Trello count closed issues in their label usage as well). Tasks whose list no
 * longer exists are not on the board anywhere and do not count.
 */
export function labelUsageCounts(board) {
  const out = {};
  for (const l of paletteOf(board)) out[l.id] = 0;
  if (!board || !Array.isArray(board.tasks)) return out;
  const groupIds = new Set((board.groups || []).map((g) => g.id));
  for (const t of board.tasks) {
    if (!groupIds.has(t.groupId)) continue;
    for (const id of labelIdsOf(t)) if (id in out) out[id] += 1;
  }
  return out;
}

/**
 * The header numbers of the Edit board modal: `{ total, done, createdAt }`,
 * plus the per-label usage. Same counts as the toolbar's summary pill
 * (boardProgress), so the two can never disagree.
 */
export function boardEditStats(board) {
  const { total, done } = boardProgress(board);
  return {
    total,
    done,
    createdAt: board?.createdAt || null,
    labelUsage: labelUsageCounts(board),
  };
}

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

/** The description's length limit, shown as "318 / 500" over the field. */
export const BOARD_DESCRIPTION_MAX = 500;

const pad2 = (n) => String(n).padStart(2, '0');

/**
 * "25 tasks · 8 done · created 04.10.2026" — parts with no data are left out.
 * The date is DD.MM.YYYY in local time, as the artboard writes it (and as the
 * board plans and comments date things).
 */
export function boardEditSubtitle(stats) {
  if (!stats) return '';
  const parts = [plural(stats.total || 0, 'task', 'tasks'), `${stats.done || 0} done`];
  const created = stats.createdAt ? new Date(stats.createdAt) : null;
  if (created && !Number.isNaN(created.getTime())) {
    parts.push(`created ${pad2(created.getDate())}.${pad2(created.getMonth() + 1)}.${created.getFullYear()}`);
  }
  return parts.join(' · ');
}

/** "When off, the 8 done tasks are hidden from this board." */
export function showDoneHint(done) {
  if (!Number.isFinite(done)) return 'When off, tasks marked done are hidden from this board.';
  if (done === 0) return 'When off, tasks marked done are hidden from this board. None are done yet.';
  return done === 1
    ? 'When off, the 1 done task is hidden from this board.'
    : `When off, the ${done} done tasks are hidden from this board.`;
}

/**
 * Move one label to another's place in the palette (drag to reorder). The
 * palette's order is the order chips are drawn on every card (resolveLabels),
 * so this is a real edit, not a view preference. Unknown ids or a move onto
 * itself return the palette unchanged.
 */
export function reorderLabels(labels, fromId, toId) {
  const list = paletteOf({ labels });
  const from = list.findIndex((l) => l.id === String(fromId));
  const to = list.findIndex((l) => l.id === String(toId));
  if (from < 0 || to < 0 || from === to) return list;
  const next = list.slice();
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return next;
}

/** Move one label up (-1) or down (+1); the keyboard twin of a drag. */
export function nudgeLabel(labels, id, delta) {
  const list = paletteOf({ labels });
  const from = list.findIndex((l) => l.id === String(id));
  const to = from + delta;
  if (from < 0 || to < 0 || to >= list.length) return list;
  return reorderLabels(list, id, list[to].id);
}
